<template>
  <StickyFooter class="verify-view" :sticky="canContinue">
    <template #header>
      <div v-if="!canView || !canChange" class="hero is-danger">
        <div class="hero-body">
          <template v-if="!canView">
            <span class="icon p-3 mr-5">
              <i class="fa-solid fa-exclamation-triangle fa-lg" />
            </span>
            <span class="text">
              You don't have the permission to view this assay
            </span>
          </template>

          <template v-else-if="!canChange">
            <span class="icon p-3 mr-5">
              <i class="fa-solid fa-exclamation-triangle fa-lg" />
            </span>
            <span class="text">
              You do not have the permission to register this assay, only the
              assay owner can proceed with registration
            </span>
          </template>
        </div>
      </div>
      <div v-else class="hero is-info">
        <div
          class="hero-body is-flex is-align-items-center p-2"
          style="margin: auto"
        >
          <span class="icon p-3 mr-5">
            <i class="fa-solid fa-info-circle fa-lg" />
          </span>
          <span class="text">
            <p>
              <b>Please carefully review the information below</b>
              <span v-if="!canDemultiplex">
                (e.g. make sure the sample number is correct)
              </span>
            </p>
            <p>
              If you spot any mistakes, please
              <span v-if="canChange">
                <b>do not proceed</b>
                but
              </span>
              <template v-if="instanceSupport?.url">
                contact
                <a
                  class="link"
                  :href="instanceSupport.url"
                  target="_blank"
                  rel="noopener"
                >
                  {{ instanceSupport.label || 'your administrator' }}
                </a>
              </template>
              <span v-else>contact your administrator</span>
              .
            </p>
            <p v-if="canDemultiplex && missingSampleNumber">
              These samples appear to be
              <b>multiplexed</b>
              , please
              <b>provide demultiplexing information</b>
              .
            </p>
          </span>
        </div>
      </div>
    </template>
    <template #body>
      <div v-if="canView" class="detail-container padded-container pt-4">
        <div class="columns mb-1">
          <div class="column">
            <div class="multi-fields">
              <div class="field">
                <label class="label is-small">Center</label>
                <div class="control">
                  <input
                    v-model="assayInfo.center"
                    class="input"
                    type="text"
                    :disabled="rawAssayInfo.managed"
                  />
                </div>
              </div>
              <div class="field">
                <label class="label is-small">Layout</label>
                <div class="control">
                  <div class="select">
                    <select :disabled="rawAssayInfo.managed">
                      <option :value="assayInfo.layout">
                        {{ assayInfo.layout }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="multi-fields">
              <div class="field">
                <label class="label is-small">Flow Cell</label>
                <div class="control">
                  <div class="select">
                    <select :disabled="rawAssayInfo.managed">
                      <option v-for="flowcell in flowcells" :key="flowcell">
                        {{ flowcell }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="field">
                <label class="label is-small">Lane</label>
                <div class="control">
                  <div class="select">
                    <select :disabled="rawAssayInfo.managed">
                      <option
                        v-for="lane in assayInfo.lanes.map((l) => l.lane)"
                        :key="lane"
                      >
                        {{ lane }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="field">
                <label class="label is-small">Run Mode</label>
                <div class="control">
                  <input
                    v-model="assayInfo.runMode"
                    class="input"
                    type="text"
                    :disabled="rawAssayInfo.managed"
                  />
                </div>
              </div>
            </div>
            <div class="multi-fields">
              <div class="field">
                <label class="label is-small">Instrument</label>
                <div class="grouped-field">
                  <div class="control" style="flex-grow: 1">
                    <p class="help">Name</p>
                    <input
                      v-model="assayInfo.instrumentName"
                      class="input"
                      type="text"
                      :disabled="rawAssayInfo.managed"
                    />
                  </div>
                  <div class="control" style="flex-grow: 1">
                    <p class="help">Model</p>
                    <div class="select">
                      <select :disabled="rawAssayInfo.managed">
                        <option :value="assayInfo.instrumentModel">
                          {{ assayInfo.instrumentModel }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="column">
            <div class="field">
              <label class="label is-small">Demultiplexing information</label>
              <div class="field if-flex is-align-items-center">
                <div class="control if-flex is-align-items-center pr-4">
                  <label class="checkbox">
                    <input
                      v-model="assayInfo.multiplexed"
                      type="checkbox"
                      :disabled="rawAssayInfo.managed && numFileSamples > 1"
                    />
                    Multiplexed
                  </label>
                </div>
                <div class="control is-flex is-align-items-center">
                  <label
                    class="checkbox mr-2"
                    :class="rawAssayInfo.managed ? 'cursor-not-allowed' : ''"
                  >
                    <input
                      v-model="assayInfo.demultiplexed"
                      type="checkbox"
                      :disabled="rawAssayInfo.managed"
                    />
                    Demultiplexed
                  </label>
                  <span class="icon hoverable-icon has-text-primary">
                    <i
                      v-if="canDemultiplex"
                      v-tooltip="
                        `These samples are multiplexed and have not
                        previously been demultiplexed.`
                      "
                      class="fa-solid fa-info-circle"
                    />
                  </span>
                </div>
              </div>
            </div>
            <div class="multi-fields">
              <div class="field mb-2">
                <label class="label is-small">Number of Samples</label>
                <div class="control">
                  <input
                    v-model="sampleNumber"
                    class="input is-small"
                    :class="{ 'is-danger': missingSampleNumber }"
                    type="number"
                    :disabled="!canDemultiplex"
                  />
                  <p v-if="missingSampleNumber" class="help is-danger">
                    This value is mandatory and has to be a positive integer.
                  </p>
                </div>
              </div>
            </div>
            <div class="multi-fields">
              <div v-if="canChange" class="form-fields mt-2">
                <div class="field">
                  <ItemDetailField
                    label="Study"
                    :is-editing="true"
                    help="Under which studies should this assay's datasets be registered?"
                  >
                    <div
                      class="is-flex is-align-items-center is-flex-wrap-wrap"
                    >
                      <SelectContainer
                        v-if="studyOptions.length > 0"
                        :key="`study-${selectedStudies
                          .map((s) => (s && s.id) || 'none')
                          .join('-')}${(studyOptions || []).length}`"
                        :value="selectedStudies"
                        :options="studyOptions"
                        placeholder="Select studies"
                        track-by="_flatId"
                        label="_displayName"
                        :multiple="true"
                        :validation-rules="[
                          {
                            name: 'required',
                            arguments: [],
                            error_message: 'Please select at least one study',
                          },
                        ]"
                        @input="(v) => (selectedStudies = v)"
                      />
                      <span class="is-flex is-align-items-center">
                        <BaseButton
                          v-tooltip="'Search for existing studies'"
                          style="margin: 0.3rem"
                          :icon="['fa-solid', 'fa-search']"
                          variant="primary"
                          size="small"
                          :outlined="true"
                          :label="
                            studyOptions.length === 0 ? 'Search' : undefined
                          "
                          @click="openPicker('search')"
                        />
                        <BaseButton
                          v-tooltip="'Create a new study'"
                          style="margin: 0.3rem"
                          :icon="['fa-solid', 'fa-plus-circle']"
                          variant="primary"
                          size="small"
                          :outlined="true"
                          :label="studyOptions.length === 0 ? 'New' : undefined"
                          @click="openPicker('create')"
                        />
                      </span>
                      <div
                        v-if="!studyOptions.length"
                        class="is-size-7 has-text-danger ml-2"
                      >
                        Please
                        <b>search</b>
                        for existing studies or create a
                        <b>new</b>
                        one
                      </div>
                    </div>
                  </ItemDetailField>
                </div>
                <RecordPickerModal
                  v-model="isOpen"
                  :initial-tab="initialTab"
                  app-name="core"
                  model-name="studies"
                  @select="addStudyOptions"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- <div v-if="canDemultiplex" class="field"> -->
        <div v-if="true" class="field">
          <label class="label is-small">
            Information for demultiplexing
            <span class="icon hoverable-icon has-text-primary">
              <i
                v-tooltip="{
                  content: sampleBarcodeHelp,
                  html: true,
                }"
                class="fa-solid fa-info-circle"
              />
            </span>
          </label>
          <div class="control" style="height: 95%">
            <!-- v-model="sampleNamesAndBarcodesText" -->
            <textarea
              style="height: 100%"
              class="textarea is-family-monospace"
              :class="{
                'is-danger': !validComaDelimitedFormat,
              }"
              :placeholder="'Name,Barcode e.g.\nSample1,ATAACG\nSample2,CCTTCG\n...'"
              :value="sampleNamesAndBarcodes"
              @input="(e) => (sampleNamesAndBarcodes = e.target.value)"
            />
            <div class="help is-danger is-flex is-flex-direction-column">
              <div v-if="!validComaDelimitedFormat">
                The expected format is
                <code>sample_name,barcode</code>
              </div>
            </div>
          </div>
        </div>
        <div class="table-section mb-6">
          <h2 class="is-size-5 m-4">
            <span class="icon">
              <i class="fa-regular fa-file" />
            </span>
            Files of flow cell
            <b>{{ flowcells[0] }}</b>
            , lane
            <b>{{ assayInfo.lanes[0].lane }}</b>
            ({{ `${numFiles} file${numFiles > 1 ? 's' : ''}` }})
          </h2>
          <div class="table-wrapper">
            <table class="table is-striped is-hoverable is-fullwidth file-list">
              <thead class="file-list-head">
                <tr key="file-table-header">
                  <th>
                    <input type="checkbox" checked disabled />
                  </th>
                  <th>Name</th>
                  <th>Format</th>
                  <th>Size</th>
                  <th>Sample</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(file, i) in assayInfo.files"
                  :key="`${i}:${file.name}`"
                >
                  <td>
                    <input type="checkbox" checked disabled />
                  </td>
                  <td style="min-width: 300px">
                    <span v-tooltip="file.uri || file.name" class="break-word">
                      {{ file.name }}
                    </span>
                  </td>
                  <td v-tooltip="`${file.format} (${file.mimetype})`">
                    <tt>{{ file.format }}</tt>
                  </td>
                  <td>{{ prettySize(file.bytes) }}</td>
                  <td>{{ file.sample }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div v-if="canChange">
        <div style="text-align: right">
          <p
            v-if="canDemultiplex && missingSampleNumber"
            class="help has-text-danger pb-4"
          >
            Please indicate a positive number of sample
          </p>
          <p
            v-if="selectedStudies.length <= 0"
            class="help has-text-danger pb-4"
          >
            Please indicate a study
          </p>
        </div>
        <div
          class="is-flex is-align-items-center is-justify-content-flex-end is-flex-wrap-wrap pb-6"
        >
          <template v-if="proposeRegisterWithTemplate">
            <span
              v-tooltip="{
                content: continueTooltip,
                html: true,
              }"
              class="icon hoverable-icon mr-4"
            >
              <i class="fa-solid fa-info-circle has-text-primary" />
            </span>
            <BaseButton
              :variant="sampleNumber > softSampleLimit ? 'primary' : undefined"
              :outlined="
                sampleNumber > softSampleLimit && sampleNumber < hardSampleLimit
              "
              :disabled="
                !canContinue || isDownloadingTemplate || isUploadingTemplate
              "
              :icon="['fa', 'fa-file-excel']"
              label="Register with spreadsheet"
              @click="
                () => {
                  displayTemplateRegister = true
                  scrollToRegisterTemplate()
                }
              "
            />
            <span class="pl-4 pr-4">or</span>
          </template>
          <span>
            <router-link
              v-if="canChange"
              :to="$attrs.next"
              event
              @click.prevent="handleSaveAssay"
            >
              <BaseButton
                v-tooltip="{
                  content: continueTooltip,
                  html: true,
                }"
                :variant="
                  proposeRegisterWithTemplate && sampleNumber > hardSampleLimit
                    ? 'warning'
                    : 'primary'
                "
                :outlined="
                  proposeRegisterWithTemplate && sampleNumber > hardSampleLimit
                "
                :loading="isContinuing"
                :disabled="
                  !(
                    canContinue &&
                    !isDownloadingTemplate &&
                    !isUploadingTemplate
                  )
                "
                :icon="['fa', 'fa-step-forward']"
                :label="
                  !proposeRegisterWithTemplate
                    ? 'Continue'
                    : 'Continue with web wizard'
                "
              />
            </router-link>
          </span>
        </div>
        <div
          v-show="displayTemplateRegister"
          ref="registerWithTemplate"
          class="p-5"
          style="
            margin: 1rem 0 2rem 0;
            border: 1px solid gray;
            border-radius: 5px;
            margin-left: auto;
          "
        >
          <span
            class="icon hoverable-icon has-text-danger is-pulled-right"
            @click="displayTemplateRegister = false"
          >
            <i class="fa-solid fa-times" />
          </span>
          <div class="title is-size-6" style="text-align: center">
            <span class="icon">
              <i class="fa-regular fa-file-excel" />
            </span>
            Register assay with spreadsheet
          </div>
          <div class="help-text pt-1 pb-1">
            <p>
              For registering your assay with a spreadsheet, you first need to
              download the assay registration template by clicking the
              <b>Download template</b>
              button below. Then fill up/edit the information in the template,
              upload the file and click the
              <b>register</b>
              button. The assay registration might take a few minutes for assays
              with many samples.
              <BaseButton
                v-tooltip="
                  !canContinue
                    ? 'Please fill up the form above to be able to download the template'
                    : ''
                "
                class="mt-2"
                :icon="['fa-solid', 'fa-download']"
                variant="success"
                size="small"
                label="Download template"
                :disabled="
                  !canContinue || isDownloadingTemplate || isUploadingTemplate
                "
                style="float: right"
                @click="downloadRegisterTemplate"
              />
            </p>
          </div>
          <div class="pt-4">
            <div class="field is-flex is-align-items-center mb-2">
              <label class="label is-small pr-4" style="flex-basis: 150px">
                Allow pool sample
              </label>
              <div class="control">
                <span
                  class="icon"
                  style="cursor: pointer"
                  @click="allowPooledSamples = !allowPooledSamples"
                >
                  <i
                    class="fa"
                    :class="{
                      'fa-toggle-on has-text-primary': allowPooledSamples,
                      'fa-toggle-off': !allowPooledSamples,
                    }"
                  />
                </span>
              </div>
            </div>
            <div class="field mb-2">
              <div class="is-flex is-align-items-center">
                <label class="label is-small pr-4" style="flex-basis: 150px">
                  Match existing sample
                </label>
                <div class="control">
                  <div class="buttons has-addons">
                    <button
                      v-for="matchOpt of ['all', 'some', 'none']"
                      :key="`button-${matchOpt}`"
                      class="button is-small"
                      :class="{
                        'is-primary is-selected': sampleMatching === matchOpt,
                      }"
                      @click="sampleMatching = matchOpt"
                    >
                      {{ $filters.capitalize(matchOpt) }}
                    </button>
                  </div>
                </div>
              </div>
              <p class="help">
                If you registered all your samples already and you are expecting
                us to match these datasets with existing samples, please pick
                the
                <b>All</b>
                option. If this is true only for some, please pick the
                <b>some</b>
                option. With
                <b>None</b>
                , the needed samples will automatically be created
              </p>
            </div>
            <div class="field is-flex is-align-items-center mb-2">
              <label class="label is-small pr-4" style="flex-basis: 150px">
                Upload template
              </label>
              <div class="control">
                <input
                  class="p-1"
                  type="file"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  @change="onTemplateFilePicked"
                />
              </div>
            </div>
          </div>
          <div class="is-flex is-justify-content-flex-end p-2">
            <BaseButton
              :icon="['fa-solid', 'fa-check']"
              variant="primary"
              size="small"
              :outlined="pickedTemplate === null"
              :loading="isUploadingTemplate"
              label="Register Assay"
              :disabled="
                isDownloadingTemplate ||
                isUploadingTemplate ||
                pickedTemplate === null
              "
              @click="onUploadTemplatePicked()"
            />
          </div>
        </div>
      </div>
    </template>
  </StickyFooter>
</template>

<script>
import { every, uniq } from 'lodash-es'
import { computed } from 'vue'
import { mapGetters, mapMutations, mapState } from 'vuex'

import { get, put } from '@/api'
import { useInstanceData } from '@/composables/useInstanceData'
import { useRecordPicker } from '@/composables/useRecordPicker'
import { prettySize } from '@/utils'
import { downloadFile } from '@/utils/api'

import StickyFooter from '@/components/data-loader/StickyFooter.vue'
import ItemDetailField from '@/components/fields/ItemDetailField.vue'
import RecordPickerModal from '@/components/RecordPickerModal.vue'
import SelectContainer from '@/components/SelectContainer.vue'

const SOFT_SAMPLE_LIMIT = 30
const HARD_SAMPLE_LIMIT = 50
const SAMPLE_BARCODE_HELP = `
  On separate lines, please provide sample names and (optionally) barcodes.
  If both are provided, use a comma separator.
  <pre class="mt-2">Sample1,ATAACG<br />Sample2,CCTTCG<br />...</pre>
`

const CONTINUE_TOOLTIP_TEXT = `
  The assay registration web wizard is the preferred way to register assay data.
  However, it is not optimized when the number of sample is large.
  In the current situation, we advise to opt for the spreadsheet registration
  if you find the wizard to behave poorly.
`

export default {
  name: 'AssayVerifyView',
  emits: ['is-loading'],
  components: {
    ItemDetailField,
    SelectContainer,
    StickyFooter,
    RecordPickerModal,
  },
  setup() {
    const { isOpen, initialTab, openPicker } = useRecordPicker()
    const { config } = useInstanceData()
    const instanceSupport = computed(() => config.value?.contact?.support)

    return {
      isOpen,
      initialTab,
      openPicker,
      instanceSupport,
    }
  },
  data() {
    return {
      isDownloadingTemplate: false,
      isUploadingTemplate: false,
      isContinuing: false,
      sampleBarcodeHelp: SAMPLE_BARCODE_HELP,
      continueTooltip: CONTINUE_TOOLTIP_TEXT,
      softSampleLimit: SOFT_SAMPLE_LIMIT,
      hardSampleLimit: HARD_SAMPLE_LIMIT,
      //
      rawAssayInfo: {
        allowed: [],
        data: [],
        managed: true,
      },
      assayInfo: {
        center: '',
        instrumentName: '',
        instrumentModel: '',
        flowcells: [],
        lanes: [],
        multiplexed: false,
        demultiplexed: false,
        layout: '',
        runMode: '',
        files: [],
        sampleNumber: 0,
      },
      selectedUsers: [],
      displayTemplateRegister: false,
      pickedTemplate: null,
      sampleMatching: 'none',
      allowPooledSamples: false,
    }
  },

  computed: {
    ...mapState('dataloader/main', {
      storeSampleNamesAndBarcodes: 'sampleNamesAndBarcodes',
      storeSelectedStudies: 'selectedStudies',
    }),
    ...mapGetters('auth', ['loggedInUser']),
    ...mapGetters('dataloader/main', {
      studyOptions: 'studyOptions',
    }),
    ...mapState('dataloader/assay', {
      assayData: (state) => state.assayData,
    }),
    ...mapGetters('dataloader/assay', {
      assayId: 'assayId',
      assayType: 'resolvedAssayType',
    }),
    assayStudies() {
      const studies = this.assayData?.studies?.value
      return Array.isArray(studies) ? studies : []
    },
    apiVerifyUrl() {
      return `assays/assays/${this.assayId}/verify/`
    },
    allowedActions() {
      return this.rawAssayInfo?.allowed || []
    },
    canView() {
      return this.allowedActions.includes('view')
    },
    canChange() {
      return this.allowedActions.includes('change')
    },
    canDemultiplex() {
      return this.assayInfo.multiplexed && !this.assayInfo.demultiplexed
    },
    canContinue() {
      return (
        !this.isContinuing &&
        this.selectedStudies.length > 0 &&
        (!this.canDemultiplex ||
          (!this.missingSampleNumber && !this.sampleNameInputErrors))
      )
    },
    flowcells() {
      return this.rawAssayInfo?.data?.map((d) => d.flowcell)
    },
    numFiles() {
      return this.assayInfo?.files?.length || 0
    },
    uniqFileListSampleNames() {
      return uniq(this.assayInfo.files.map((f) => f.sample))
    },
    numFileSamples() {
      /* This property gives information about the demultiplexing state
       * If the assay has been demultiplexed, the file list will have at least
       * two different samples. However if it was not demultiplexed it will
       * only have one (even if 3 files are present) */
      return this.uniqFileListSampleNames.length
    },
    sampleNumber: {
      get() {
        /* When multiplexed was unchecked, then canDemultiplexed is false,
         * the sample number is then 1. because multiplexed cannot be unchecked
         * when there is more than one file */
        if (this.canDemultiplex || this.assayInfo.demultiplexed === true) {
          return Number.parseInt(this.assayInfo.sampleNumber, 10)
        } else {
          return 1
        }
      },
      set(num) {
        this.assayInfo.sampleNumber = Number.parseInt(num, 10)
      },
    },
    missingSampleNumber() {
      return (
        this.canDemultiplex && (!this.sampleNumber || this.sampleNumber < 1)
      )
    },
    sampleNamesAndBarcodes: {
      get() {
        return this.storeSampleNamesAndBarcodes
      },
      set(value) {
        this.setSampleNamesAndBarcodes(value)
      },
    },
    sampleNamesAndBarcodesLines() {
      return (
        (this.sampleNamesAndBarcodes.length &&
          this.sampleNamesAndBarcodes?.trim()?.split('\n')) ||
        []
      )
    },
    sampleNamesAndBarcodesSplittedLines() {
      return this.sampleNamesAndBarcodesLines.map(
        (line) => line?.split(',')?.map((v) => v.trim()) || [],
      )
    },
    validComaDelimitedFormat() {
      return every(
        this.sampleNamesAndBarcodesSplittedLines,
        (lineArr) => lineArr.length === 2 && every(lineArr),
      )
    },
    sampleNames() {
      return this.sampleNamesAndBarcodesSplittedLines.map(
        (sl) => sl.length > 0 && sl[0],
      )
    },
    sampleNameInputErrors() {
      return (
        // !this.hasEnoughSamplesInfo ||
        !this.validComaDelimitedFormat
        // || this.hasDuplicatedSampleNames
      )
    },
    sampleBarcodes() {
      return this.sampleNamesAndBarcodesSplittedLines.map(
        (sl) => sl.length > 1 && sl[1],
      )
    },
    sampleAndBarcodePayload() {
      let samplePayload = {}
      return this.sampleNames.reduce((samplePayload, sampleName, index) => {
        samplePayload[sampleName] = {
          barcode: this.sampleBarcodes[index] || '',
        }
        return samplePayload
      }, samplePayload)
    },
    rawSamplePayload() {
      return this.rawAssayInfo.data[0].lanes[0].samples
    },
    needRawSamplePayloadCorrection() {
      return (
        this.assayInfo.multiplexed === false &&
        Object.keys(this.rawSamplePayload).length !== this.sampleNumber
      )
    },
    selectedStudies: {
      get() {
        return this.storeSelectedStudies || []
      },
      set(studies) {
        this.selectStudies(studies)
      },
    },
    registerBasePath() {
      return `assays/assays/${this.assayId}/register/`
    },
    uploadBasePath() {
      return `${this.registerBasePath}?format=json&allow_pooled_samples=${this.allowPooledSamples}`
    },
    proposeRegisterWithTemplate() {
      return !this.canDemultiplex && this.sampleNumber > this.softSampleLimit
    },
    downloadRegisterTemplatePath() {
      const getTemplateBasePath = `${this.registerBasePath}?format=xlsx`
      const ownerSep = '&owner='
      const ownerPath =
        ownerSep + this.selectedUsers.map((o) => o.id).join(ownerSep)
      const studySep = '&study='
      const studyPath =
        studySep + this.selectedStudies.map((s) => s.id.value).join(studySep)
      return getTemplateBasePath + ownerPath + studyPath
    },
  },
  methods: {
    prettySize,
    ...mapMutations('dataloader/main', {
      setSampleNamesAndBarcodes: 'SET_SAMPLENAMES_AND_BARCODES',
      selectStudies: 'SELECT_STUDIES',
      storeAddStudyOptions: 'ADD_STUDY_OPTIONS',
    }),
    async fetchAssayInfo() {
      const response = await get(this.apiVerifyUrl).catch((error) => {
        const message = error.response.data.message
        this.$store.commit('notify', { type: 'error', message })
      })
      return response.data
    },
    initAssayInfo(rawAssayInfo) {
      this.rawAssayInfo = rawAssayInfo
      const rawAssayData =
        Object.keys(rawAssayInfo).length > 0 &&
        rawAssayInfo.data &&
        rawAssayInfo.data.length &&
        rawAssayInfo.data[0]
      if (rawAssayData) {
        const firstLane = rawAssayData.lanes[0]
        this.assayInfo = {
          center: rawAssayData.producer.name,
          instrumentName: rawAssayData.sequencer.name,
          instrumentModel: rawAssayData.sequencer.model,
          lanes: rawAssayData.lanes,
          multiplexed: firstLane.multiplexed === true,
          demultiplexed: firstLane.demultiplexed === true,
          layout: firstLane.type,
          runMode: firstLane.runmode,
          files: firstLane.filelist,
          sampleNumber: firstLane.samplenumber || 0,
          samples: firstLane.samples,
        }
      }
    },
    async loadVerifyAssay() {
      const rawAssayInfo = await this.fetchAssayInfo()
      this.initAssayInfo(rawAssayInfo)
    },
    addStudyOptions(studies) {
      this.storeAddStudyOptions(studies)
      if (this.selectedStudies.length === 0)
        this.selectedStudies = this.studyOptions
    },
    formatAssayPayload() {
      this.assayPayload = { ...this.rawAssayInfo }
      this.assayPayload.data[0].lanes[0].multiplexed =
        this.assayInfo.multiplexed

      this.assayPayload.data[0].lanes[0].samplenumber = this.sampleNumber
      /* Modify samples payload */
      if (this.canDemultiplex) {
        /* in case user inputs sample information */
        this.assayPayload.data[0].lanes[0].samples =
          this.sampleAndBarcodePayload
      } else if (this.needRawSamplePayloadCorrection) {
        /* Hitting this part when the assay is not multiplexed
         * the default sample object SHOULD be okay, except if the user
         * already has verified the assay once and provided dmultiplexing info
         * in which case the previously given info is false and needs to
         * be generated from scratch */
        let correctPayload = {}
        this.uniqFileListSampleNames.reduce((correctPayload, sampleName) => {
          correctPayload[sampleName] = { barcode: '' }
          return correctPayload
        }, correctPayload)
        this.assayPayload.data[0].lanes[0].samples = correctPayload
      }
      return this.assayPayload
    },
    handleSaveAssay() {
      this.isContinuing = true

      const assayPayload = this.formatAssayPayload()
      return put(this.apiVerifyUrl, { data: assayPayload })
        .then(() => {
          this.$router.push(this.$attrs.nextRoute)
        })
        .catch((err) => {
          this.isContinuing = false
          this.$store.commit('notify', {
            type: 'error',
            message: err.response.data.message,
          })
        })
    },
    downloadRegisterTemplate() {
      this.isDownloadingTemplate = true
      return downloadFile({ url: this.downloadRegisterTemplatePath })
        .catch((err) => {
          console.error('err', err)
          this.$store.commit('notify', {
            type: 'error',
            message: err.response.data.message,
          })
        })
        .finally(() => {
          this.isDownloadingTemplate = false
        })
    },
    scrollToRegisterTemplate() {
      this.$nextTick(() =>
        this.$refs.registerWithTemplate.scrollIntoView({
          behavior: 'smooth',
        }),
      )
    },
    onTemplateFilePicked(event) {
      this.pickedTemplate = event.target.files[0]
    },
    onUploadTemplatePicked() {
      this.isUploadingTemplate = true
      let uploadFile = new FormData()
      uploadFile.append('file', this.pickedTemplate)
      uploadFile.append('match_samples', this.sampleMatching)
      return put(this.uploadBasePath, {
        data: uploadFile,
      })
        .then(() => {
          this.$store.commit('notify', {
            type: 'success',
            message: 'Successfully registered assay',
          })
          this.$router.push({
            name: 'assayDetail',
            params: {
              id: this.assayId,
              modelType: this.assayType,
            },
          })
        })
        .catch((err) => {
          this.$store.commit('notify', {
            type: 'error',
            message: err.response.data.message,
          })
        })
        .finally(() => {
          this.isUploadingTemplate = false
        })
    },
  },
  async beforeMount() {
    this.$emit('is-loading', true)
    this.selectedUsers = [this.loggedInUser]
    try {
      await this.loadVerifyAssay()
    } catch (e) {
      console.error(e)
    }
    // Pre-populate studies from assay record
    if (this.assayStudies.length > 0 && this.studyOptions.length === 0) {
      this.addStudyOptions(this.assayStudies)
    }
    this.$emit('is-loading', false)
  },
  watch: {
    isContinuing: function (c) {
      this.$emit('is-loading', c)
    },
  },
}
</script>

<style lang="scss" scoped>
.verify-view .hero a {
  color: var(--brand) !important;
}

.verify-view h1 {
  font-family: var(--font-heading);
}

.detail-container {
  overflow: auto;
}

.table-wrapper {
  overflow-y: scroll;
  max-height: 500px;
  border-bottom: 1px solid var(--color-border-default);
}

table.table.file-list tbody {
  width: 100%;
}

table.table.file-list thead.file-list-head th {
  position: sticky;
  top: 0;
  background-color: white;
}

.select,
.select select,
.input,
.input input {
  width: 100%;
}

.multi-fields {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.multi-fields .field {
  flex-grow: 1;
}

.grouped-field {
  display: flex;
  flex-wrap: wrap;
}

.column .multi-fields .field,
.grouped-field .control {
  padding: 0 0.5rem;
}
.column .multi-fields .field:first-of-type,
.grouped-field .control:first-of-type {
  padding-left: 0rem;
}
.column .multi-fields .field:last-of-type,
.grouped-field .control:last-of-type {
  padding-right: 0rem;
}

.template-registration-box {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  margin: 0.5rem;
  padding: 0.5rem;
  padding-bottom: 1rem;
  margin-bottom: 2rem;
  border: 2px solid var(--tw-color-green-600);
}

.template-registration-step {
  padding-right: 1rem;
  flex-basis: 20%;
  min-width: 300px;
  flex-grow: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
}

.cursor-not-allowed {
  cursor: not-allowed;
}
</style>
